import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Panel } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

async function stepUp(pw) {
  const { data } = await api.post("/auth/step-up/risk-control", /^\d{6}$/.test(pw) ? { totp_code: pw } : { password: pw });
  return data.step_up_token;
}

function Bar({ label, used, limit, testId }) {
  const pct = Math.min(100, (Number(used) / Math.max(Number(limit), 0.0001)) * 100);
  return (
    <div data-testid={testId} className="space-y-1">
      <div className="flex justify-between text-slate-400"><span>{label}</span><span className="font-mono">{used}% / {limit}%</span></div>
      <div className="h-1.5 rounded bg-slate-800"><div className={`h-1.5 rounded ${pct > 80 ? "bg-rose-500" : "bg-sky-500"}`} style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

export function AllocationPanel({ book }) {
  const qc = useQueryClient();
  const [pw, setPw] = useState("");
  const a = useQuery({ queryKey: ["cap-alloc", book], queryFn: async () => (await api.get(`/capital/allocation/${book}`)).data });
  const [plan, setPlan] = useState(null);
  const propose = useMutation({ mutationFn: async () => (await api.post(`/capital/rebalance/${book}/propose`)).data,
    onSuccess: setPlan, onError: (e) => toast.error(formatApiError(e.response?.data?.detail)) });
  const exec = useMutation({
    mutationFn: async () => (await api.post(`/capital/rebalance/plans/${plan._id}/execute`, { step_up_token: await stepUp(pw) })).data,
    onSuccess: (p) => { setPlan(p); setPw(""); toast.success(`Rebalance ${p.status}`); qc.invalidateQueries({ queryKey: ["cap-alloc", book] }); qc.invalidateQueries({ queryKey: ["cap-report", book] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)) });
  if (a.isLoading) return <div data-testid={`allocation-loading-${book}`} className="text-xs text-slate-500">Loading allocation…</div>;
  if (a.isError) return <div data-testid={`allocation-error-${book}`} className="text-xs text-rose-400">Allocation unavailable: {formatApiError(a.error?.response?.data?.detail)}</div>;
  const d = a.data;
  return (
    <Panel title={`Allocation · policy v${d.policy_version}`} testId={`allocation-panel-${book}`}>
      <div className="p-4 space-y-4 text-xs">
        <div className="grid sm:grid-cols-3 gap-3">
          {Object.entries(d.risk_utilization).map(([k, v]) => <Bar key={k} testId={`risk-util-${k}`} label={k.replace("_", " ")} used={v.used} limit={v.limit} />)}
        </div>
        <SleeveTable rows={d.sleeves} />
        {d.unallocated_positions.length > 0 && <div data-testid="unallocated" className="text-amber-300">Not in any sleeve (never sold automatically): {d.unallocated_positions.map((p) => p.symbol).join(", ")}</div>}
        <div className="flex flex-wrap gap-2 items-center">
          <Button data-testid="rebalance-propose-btn" size="sm" variant="outline" disabled={!d.policy_version || propose.isPending} onClick={() => propose.mutate()}>Preview rebalance</Button>
          {!d.policy_version && <span className="text-slate-500">Approve an allocation policy first.</span>}
        </div>
        {plan && <PlanView plan={plan} pw={pw} setPw={setPw} onExec={() => exec.mutate()} busy={exec.isPending} />}
      </div>
    </Panel>
  );
}

function SleeveTable({ rows }) {
  if (!rows.length) return <div data-testid="sleeves-empty" className="text-slate-500">No sleeves configured.</div>;
  return (
    <div className="overflow-x-auto"><table className="w-full" data-testid="sleeve-table">
      <thead><tr className="text-left text-slate-500"><th className="py-1 pr-3">Sleeve</th><th className="pr-3">Target %</th><th className="pr-3">Max %</th><th className="pr-3">Current %</th><th className="pr-3">Drift %</th><th>Status</th></tr></thead>
      <tbody>{rows.map((s) => (
        <tr key={`${s.kind}-${s.key}`} className="border-t border-[var(--border-subtle)] text-slate-300" data-testid={`sleeve-${s.key}`}>
          <td className="py-1.5 pr-3 font-mono">{s.kind}:{s.key}</td><td className="pr-3">{s.target_pct}</td><td className="pr-3">{s.max_pct}</td>
          <td className="pr-3">{s.current_pct}</td><td className="pr-3">{s.drift_pct}</td>
          <td className={s.max_breached ? "text-rose-400" : s.eligible ? "text-emerald-400" : "text-amber-400"}>{s.max_breached ? "over max" : s.eligible ? "ok" : s.note}</td>
        </tr>))}</tbody>
    </table></div>
  );
}

function PlanView({ plan, pw, setPw, onExec, busy }) {
  return (
    <div data-testid="rebalance-plan" className="rounded-md border border-[var(--border-subtle)] p-3 space-y-2">
      <div className="text-slate-400">Plan {plan._id} · {plan.status} · turnover scale {plan.turnover_scale} · paper only</div>
      {plan.legs.map((l, i) => <div key={i} className="font-mono text-slate-300" data-testid={`plan-leg-${i}`}>{l.side} {l.qty} {l.symbol} ≈ {l.notional}{l.stop_loss ? ` · stop ${l.stop_loss}` : ""}{plan.results ? ` → ${plan.results[i]?.status}` : ""}</div>)}
      {plan.status === "proposed" && (
        <div className="flex gap-2">
          <Input data-testid="rebalance-reverify-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password or 6-digit code" className="h-8 bg-[var(--bg-canvas)] border-[var(--border-subtle)]" />
          <Button data-testid="rebalance-execute-btn" size="sm" disabled={!pw || busy} onClick={onExec}>Execute on paper</Button>
        </div>)}
    </div>
  );
}
